import type { ReactNode } from "react";
import { Icon, type TablerIcon } from "../icon";
import { cx } from "../tv";

/** Label and value rows, each value editable in place by the caller (DESIGN-RULES 9). */
export function PropertyList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cx("grid grid-cols-[112px_minmax(0,1fr)] gap-y-2.5 text-base", className)}>{children}</dl>;
}

export function Property({ icon, label, children }: { icon?: TablerIcon; label: string; children: ReactNode }) {
  return (
    <>
      <dt className="flex min-h-7 items-center gap-2 text-fg-muted">
        {icon && <Icon icon={icon} size="chip" className="text-fg-subtle" />}
        <span className="truncate-1">{label}</span>
      </dt>
      <dd className="flex min-h-7 min-w-0 flex-wrap items-center gap-2">{children}</dd>
    </>
  );
}

/** Document title in the side panel or a record page: Radio Canada Big, balanced, up to two lines (DESIGN-RULES 6). */
export function DocTitle({ children, size = "panel" }: { children: ReactNode; size?: "panel" | "page" }) {
  return <h2 className={cx("font-title mb-4.5 leading-[1.08]", size === "panel" ? "text-4xl max-laptop:text-3xl" : "text-5xl")}>{children}</h2>;
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h3 className="font-title mb-1.5 mt-5 text-xl tracking-[-0.015em]">{children}</h3>;
}

/** Reading text: 15 px at 1.62 (DESIGN-RULES 6), at most 720 px wide on full pages. */
export function Prose({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("max-w-(--read-w) text-lg leading-read text-fg [&_p+p]:mt-2.5", className)}>{children}</div>;
}

/** Callout for client feedback and notes (Saffron edge). */
export function Callout({ icon, title, children, hue = "saffron" }: { icon?: TablerIcon; title: ReactNode; children: ReactNode; hue?: "saffron" | "sky" | "ember" | "fern" }) {
  return (
    <div
      className="flex gap-2.5 rounded-lg px-3.5 py-3"
      style={{ background: `var(--code-${hue}-subtle)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--code-${hue}-border) 70%, transparent)` }}
    >
      {icon && <Icon icon={icon} className="mt-0.5" style={{ color: `var(--code-${hue}-text)` }} />}
      <div className="min-w-0">
        <div className="text-base font-semibold">{title}</div>
        <div className="mt-0.5 text-md leading-[1.5] text-fg">{children}</div>
      </div>
    </div>
  );
}
